import {
  LayoutDashboard,
  Zap,
  ListChecks,
  ShieldCheck,
  Rocket,
  SlidersHorizontal,
  FileText,
  type LucideIcon,
} from 'lucide-react';

// Domain-level navigation, grouped exactly like the legacy
// External.SwiftFederation sidebar (Caching / Rules / Security / Delivery +
// standalone Optimization & Operations). Slugs are relative to the domain
// base `/services/domains/:domainId`.

export interface DomainNavLeaf {
  label: string;
  /** Relative slug under the domain base. '' = overview index. */
  slug: string;
  beta?: boolean;
}

export interface DomainNavEntry {
  label: string;
  icon: LucideIcon;
  /** A standalone item (no children) when `slug` is set + `items` empty. */
  slug?: string;
  beta?: boolean;
  items?: DomainNavLeaf[];
}

export const DOMAIN_NAV: DomainNavEntry[] = [
  { label: 'Overview', icon: LayoutDashboard, slug: '' },
  {
    label: 'Caching',
    icon: Zap,
    items: [
      { label: 'Cache', slug: 'cache' },
      { label: 'Origin', slug: 'upstream' },
      { label: 'Origin Rewrite', slug: 'origin-rewrite' },
    ],
  },
  {
    label: 'Rules',
    icon: ListChecks,
    items: [
      { label: 'Header Rules', slug: 'header-rules' },
      { label: 'Error Pages', slug: 'error-pages' },
      { label: 'Redirects', slug: 'redirects' },
    ],
  },
  {
    label: 'Security',
    icon: ShieldCheck,
    items: [
      { label: 'WAF', slug: 'security/waf' },
      { label: 'Firewall Rules', slug: 'security/firewall-rules' },
      { label: 'WAF Analytics', slug: 'security/waf-analytics' },
      { label: 'Rate Limiting', slug: 'security/rate-limiting' },
      { label: 'Access Control', slug: 'security/access-control' },
      { label: 'CORS', slug: 'security/cors' },
      { label: 'Security Headers', slug: 'security/security-headers' },
      { label: 'Verified Bots', slug: 'security/verified-bots' },
      { label: 'Bot Management', slug: 'security/bot-management' },
      { label: 'Bot Mgmt Analytics', slug: 'security/bot-management-analytics' },
    ],
  },
  {
    label: 'Delivery',
    icon: Rocket,
    items: [
      { label: 'Purge & Warm', slug: 'purge' },
      { label: 'SSL / TLS', slug: 'ssl' },
    ],
  },
  { label: 'Optimization', icon: SlidersHorizontal, slug: 'optimization', beta: true },
  { label: 'Operations', icon: FileText, slug: 'operations', beta: true },
];

/** Absolute href for a domain nav slug (`''` = overview/base). */
export function domainHref(base: string, slug: string): string {
  return slug ? `${base}/${slug}` : base;
}

/** Every slug (top-level + nested) for active-state matching. */
function allDomainSlugs(): string[] {
  return DOMAIN_NAV.flatMap((e) => (e.items?.length ? e.items.map((i) => i.slug) : [e.slug ?? '']));
}

/** The active entry href for the current pathname (longest match wins). */
export function domainActiveId(base: string, pathname: string): string {
  const matches = allDomainSlugs()
    .map((slug) => domainHref(base, slug))
    .filter((href) => pathname === href || pathname.startsWith(`${href}/`))
    .sort((a, b) => b.length - a.length);
  return matches[0] ?? base;
}
